import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import type { DateRange } from "react-day-picker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  useInsuredNotes,
  useInsuredActivityLogs,
  useInsuredCOIHistory,
  useInsuredDocumentsForTimeline,
  fetchPolicyChangeHistory,
  fetchPolicySyncEvents,
  fetchPolicyRecordUpdates,
  type PolicyChangeHistoryEntry,
  type PolicySyncEvent,
} from "@/lib/api";
import type { PolicyRecordUpdateRow } from "@shared/recordUpdate";
import type { PolicyDocument, Policy } from "@shared/schema";
import {
  activityLogsToEvents,
  aorDetectedToEvents,
  applyTimelineFilters,
  coiIssuancesToEvents,
  docsToEvents,
  groupByDay,
  insuredDocsToEvents,
  mergeSources,
  notesToEvents,
  policyChangeHistoryToEvents,
  policyRecordUpdatesToEvents,
  policySyncEventsToEvents,
  type TimelineEvent,
} from "./timeline-events";
import { TimelineEventCard } from "./TimelineEvent";
import { TimelineFilterChips, type ChipKey } from "./TimelineFilterChips";
import { TimelinePolicyFilter } from "./TimelinePolicyFilter";
import { TimelineDateRangeFilter } from "./TimelineDateRangeFilter";
import { apiFetch } from "@/lib/queryClient";
import { AddNoteModal } from "@/components/insured/notes/AddNoteModal";
import {
  aorDetectionTimes,
  withoutAorChanges,
} from "@/components/insured/policies/aor";

interface Props {
  insuredId: string;
  policies: Policy[];
  /** CSR-2332 — the AOR experience flag, read once on the page. */
  showAor?: boolean;
}

const API_BASE = "/api";
const INITIAL_LIMIT = 10;
// CSR-1445 (R-002) — hard cap on how many activity-log rows the timeline
// fetches. The server clamps `limit` to 200, so this is the real ceiling; if an
// insured has more matching rows the UI labels the truncation honestly rather
// than silently dropping the overflow.
const LOG_FETCH_LIMIT = 200;

function fetchPolicyDocuments(policyId: string): Promise<PolicyDocument[]> {
  return apiFetch(`${API_BASE}/policies/${policyId}/documents`, {
    credentials: "include",
  }).then((r) => {
    if (!r.ok) throw new Error("Failed to fetch documents");
    return r.json();
  });
}

export function ActivityTimeline({
  insuredId,
  policies,
  showAor = false,
}: Props) {
  const [active, setActive] = useState<ChipKey>("all");
  // CSR-1940 — By-policy filter axis. `"all"` = no policy narrowing.
  const [policyId, setPolicyId] = useState<string>("all");
  // CSR-1940 — Date-range filter axis (react-day-picker { from?, to? }).
  const [range, setRange] = useState<DateRange | undefined>();
  const [showAll, setShowAll] = useState(false);

  const { data: notes = [], isLoading: notesLoading } = useInsuredNotes(insuredId);
  // CSR-773: include soft-deleted rows so the timeline keeps showing
  // the `Document Uploaded` event after the user deletes the document.
  const { data: insuredDocs = [], isLoading: insuredDocsLoading } =
    useInsuredDocumentsForTimeline(insuredId);

  // CSR-1445 — insured-scoped feed: the server filters to this insured's own
  // events + its policies' events (no agency filter), so the timeline is
  // reliable regardless of how much unrelated activity exists and which agency
  // is selected in the global picker. Replaces the old global top-100 fetch +
  // client-side filter, which silently dropped events outside the global window.
  const { data: activityLogsResp, isLoading: logsLoading } =
    useInsuredActivityLogs(insuredId, LOG_FETCH_LIMIT);

  // CSR-1908 — insured COI issuance history feeds the "COI sent" Docs card. The
  // consolidated `coi.sent`/`coi_request.sent` activity-log rows are written on
  // entity types the insured feed never selects, so the card must be sourced
  // here (one card per completed issuance — no double-count with the feed).
  // Fetched in full like notes/change-history/docs (not subject to the
  // LOG_FETCH_LIMIT activity-log truncation notice).
  const { data: coiHistory = [], isLoading: coiHistoryLoading } =
    useInsuredCOIHistory(insuredId);

  // Per-policy document fetches — one query per policy. Small N in practice.
  const docQueries = useQueries({
    queries: policies.map((p) => ({
      queryKey: ["policy-documents", p.id],
      queryFn: () => fetchPolicyDocuments(p.id),
      enabled: !!p.id,
    })),
  });
  const docsLoading = docQueries.some((q) => q.isLoading);
  const docsFlat = useMemo(() => {
    const out: Array<PolicyDocument & { policyNumber?: string }> = [];
    docQueries.forEach((q, i) => {
      const list = (q.data as PolicyDocument[] | undefined) ?? [];
      const policyNumber = policies[i]?.policyNumber;
      for (const d of list) out.push({ ...d, policyNumber });
    });
    return out;
  }, [docQueries, policies]);

  // CSR-1843 — per-policy change-history fan-out, mirroring the document fetches
  // above. Keyed on the SAME query key as `usePolicyChangeHistory` so the cache
  // is shared with the Policy Activity tab (tab navigation is free). Small N in
  // practice (the insured's policy count).
  const changeHistoryQueries = useQueries({
    queries: policies.map((p) => ({
      queryKey: ["policy-change-history", p.id],
      queryFn: () => fetchPolicyChangeHistory(p.id),
      enabled: !!p.id,
    })),
  });
  const changeHistoryLoading = changeHistoryQueries.some((q) => q.isLoading);
  const policyChangeEvents = useMemo(() => {
    const out: TimelineEvent[] = [];
    changeHistoryQueries.forEach((q, i) => {
      const rows = (q.data as PolicyChangeHistoryEntry[] | undefined) ?? [];
      const p = policies[i];
      if (!p) return;
      out.push(
        ...policyChangeHistoryToEvents(
          showAor ? withoutAorChanges(rows) : rows,
          { carrier: p.carrier, parentCarrier: p.parentCarrier },
          p.policyNumber,
          p.id, // CSR-1940 — By-policy filter key
        ),
      );
      if (showAor) {
        out.push(
          ...aorDetectedToEvents(
            aorDetectionTimes(rows, {
              identifiedAt: p.aorIdentifiedAt ?? null,
              source: p.aorSource ?? null,
            }),
            p.policyNumber,
            p.id,
          ),
        );
      }
    });
    return out;
  }, [changeHistoryQueries, policies, showAor]);

  // CSR-1940 (D1=B) — per-policy sync-event fan-out, mirroring the change-history
  // fetches above. Keyed on the SAME query key as `usePolicySyncEvents` so the
  // cache is shared with the Policy Activity tab. `policySyncEventsToEvents`
  // keeps only non-success outcomes (failures), so successful carrier syncs stay
  // represented by their change cards (policyChangeEvents) — no double-count.
  const syncEventQueries = useQueries({
    queries: policies.map((p) => ({
      queryKey: ["policy-sync-events", p.id],
      queryFn: () => fetchPolicySyncEvents(p.id),
      enabled: !!p.id,
    })),
  });
  const syncEventsLoading = syncEventQueries.some((q) => q.isLoading);
  const policySyncEvents = useMemo(() => {
    const out: TimelineEvent[] = [];
    syncEventQueries.forEach((q, i) => {
      const rows = (q.data as PolicySyncEvent[] | undefined) ?? [];
      const p = policies[i];
      if (!p || rows.length === 0) return;
      out.push(
        ...policySyncEventsToEvents(
          rows,
          { carrier: p.carrier, parentCarrier: p.parentCarrier },
          p.id,
        ),
      );
    });
    return out;
  }, [syncEventQueries, policies]);

  // CSR-1955 — per-policy record-update fan-out (shares the policy tab's query
  // key for a warm cache), mirroring the change-history/sync fan-outs above.
  const recordUpdateQueries = useQueries({
    queries: policies.map((p) => ({
      queryKey: ["policy-record-updates", p.id],
      queryFn: () => fetchPolicyRecordUpdates(p.id),
      enabled: !!p.id,
    })),
  });
  const recordUpdatesLoading = recordUpdateQueries.some((q) => q.isLoading);
  const policyRecordUpdateEvents = useMemo(() => {
    const out: TimelineEvent[] = [];
    recordUpdateQueries.forEach((q, i) => {
      const rows = (q.data as PolicyRecordUpdateRow[] | undefined) ?? [];
      const p = policies[i];
      if (!p || rows.length === 0) return;
      out.push(
        ...policyRecordUpdatesToEvents(rows, insuredId, p.policyNumber, p.id),
      );
    });
    return out;
  }, [recordUpdateQueries, policies, insuredId]);

  const merged = useMemo(() => {
    const policyIdSet = new Set(policies.map((p) => p.id));
    const activityLogs = activityLogsResp?.data ?? [];
    return mergeSources([
      notesToEvents(notes),
      activityLogsToEvents(activityLogs, insuredId, policyIdSet),
      docsToEvents(docsFlat),
      // CSR-2064 — the policies let a generated ACORD form's stored policy ids
      // render as numbers and carriers instead of raw `dc-` ids.
      insuredDocsToEvents(insuredDocs, policies),
      policyChangeEvents,
      policySyncEvents,
      policyRecordUpdateEvents,
      coiIssuancesToEvents(coiHistory),
    ]);
  }, [
    notes,
    activityLogsResp,
    docsFlat,
    insuredDocs,
    policyChangeEvents,
    policySyncEvents,
    policyRecordUpdateEvents,
    coiHistory,
    insuredId,
    policies,
  ]);

  // CSR-1445 (R-002) — the activity-log feed is capped at `LOG_FETCH_LIMIT`
  // (the hook's default `limit`). When the server reports more matching rows
  // than we fetched, older activity-log events are NOT loaded, so the timeline
  // must NOT claim completeness. We surface an honest "showing the N most
  // recent" notice instead of a "Show All N Events" label that would imply the
  // user is seeing everything. (Notes / change-history / documents are fetched
  // in full, so only the activity-log source can be truncated.)
  const fetchedLogs = activityLogsResp?.data.length ?? 0;
  const totalLogs = activityLogsResp?.total ?? 0;
  const logsTruncated = totalLogs > fetchedLogs;

  // CSR-1940 — the single filtering truth. Chip AND policy AND date range all
  // compose.
  const filtered = useMemo(
    () =>
      applyTimelineFilters(merged, {
        active,
        policyId,
        from: range?.from,
        to: range?.to,
      }),
    [merged, active, policyId, range]
  );

  // CSR-1940 — layout-stable empty copy: distinguish "nothing here" from
  // "nothing matches your filters" so the user knows a filter is hiding rows.
  const anyFilterActive =
    active !== "all" || policyId !== "all" || !!range?.from;

  // CSR-1885 — pinned notes float to the very top as bare cards (most recently
  // pinned first), above the day-grouped chronological list. They respect the
  // active filter (partitioned out of `filtered`) and are always shown in full,
  // independent of the Show-More trimming applied to the rest.
  const pinnedNotes = useMemo(
    () =>
      filtered
        .filter((e) => e.kind === "notes" && e.noteMeta?.pinned)
        .sort((a, b) =>
          (b.noteMeta!.pinnedAt ?? "").localeCompare(a.noteMeta!.pinnedAt ?? ""),
        ),
    [filtered]
  );
  const rest = useMemo(
    () => filtered.filter((e) => !(e.kind === "notes" && e.noteMeta?.pinned)),
    [filtered]
  );

  const totalVisible = rest.length;
  const flatTrimmed = showAll ? rest : rest.slice(0, INITIAL_LIMIT);
  const visibleGroups = useMemo(
    () => groupByDay(flatTrimmed),
    [flatTrimmed]
  );

  const isLoading =
    notesLoading ||
    logsLoading ||
    docsLoading ||
    insuredDocsLoading ||
    changeHistoryLoading ||
    syncEventsLoading ||
    recordUpdatesLoading ||
    coiHistoryLoading;
  const totalEvents = filtered.length;

  return (
    <Card className="p-6" data-testid="activity-timeline">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-base font-semibold">Activity Timeline</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isLoading
              ? "Loading…"
              : `${totalEvents} event${totalEvents === 1 ? "" : "s"} recorded`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TimelineFilterChips active={active} onChange={setActive} insuredId={insuredId} />
          <TimelinePolicyFilter
            policies={policies}
            value={policyId}
            onChange={setPolicyId}
          />
          <TimelineDateRangeFilter value={range} onChange={setRange} />
          <AddNoteModal scope="insured" insuredId={insuredId} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : pinnedNotes.length === 0 && visibleGroups.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-8">
          {anyFilterActive ? "No activity matches these filters" : "No activity yet"}
        </div>
      ) : (
        <div className="space-y-6">
          {pinnedNotes.length > 0 && (
            <div
              className="space-y-3 border-l border-border pl-4 ml-1"
              data-testid="timeline-pinned"
            >
              {pinnedNotes.map((e) => (
                <TimelineEventCard key={e.id} event={e} />
              ))}
            </div>
          )}
          {visibleGroups.map((g) => (
            <div key={g.dayKey} className="space-y-3">
              <p className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                {g.dayLabel}
              </p>
              <div className="space-y-3 border-l border-border pl-4 ml-1">
                {g.events.map((e) => (
                  <TimelineEventCard key={e.id} event={e} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalVisible > INITIAL_LIMIT && (
        <div className="flex justify-center pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            data-testid="timeline-toggle-show-all"
            className="cursor-pointer"
          >
            {showAll
              ? "Show Less"
              : // CSR-1445 (R-002): only claim a complete "Show All N" count when
                // the activity-log feed is NOT truncated. When older events were
                // left unfetched, drop the count so the label can't imply the
                // user is seeing everything — the notice below states the cap.
                logsTruncated
                ? "Show More"
                : `Show All ${totalVisible} Events`}
          </Button>
        </div>
      )}

      {!isLoading && logsTruncated && (
        <p
          className="text-xs text-muted-foreground text-center pt-3"
          data-testid="timeline-truncation-notice"
        >
          Showing the {fetchedLogs} most recent activity events of {totalLogs} —
          older events are not shown.
        </p>
      )}
    </Card>
  );
}
